import { useCallback, useMemo, useState } from 'react';
import { ManageMembershipRequestType } from 'shared/types/graphql';
import { ActivityLogModel, CareActivityLogItem } from '../components/ActivityLog/types';

export enum CareActivityLogFilterType {
  BenefitUsage = 'BenefitUsage',
  EnrollmentEvent = 'EnrollmentEvent',
  CareEnrollmentPayment = 'CareEnrollmentPayment',
  Note = 'Note',
}

export interface CareActivityLogFiltersInput {
  items: CareActivityLogItem[];
  take: number;
}

export interface CareActivityLogFiltersOutput {
  filteredItems: CareActivityLogItem[];
  filteredItemsTotalCount: number;
  selectedFilters: CareActivityLogFilterType[];
  isAllFiltersSelected: boolean;
  handleFilterChange: (filter: CareActivityLogFilterType) => void;
  onSelectAllFilters: () => void;
  onClearAllFilters: () => void;
}

const filterItems = (
  items: CareActivityLogItem[],
  selectedFilters: CareActivityLogFilterType[],
): CareActivityLogItem[] => {
  return items.filter((item) => {
    if (item.data.type === ActivityLogModel.EnrollmentSupportEvent) {
      return (
        (item.data.manageMembershipRequestType !== ManageMembershipRequestType.Note &&
          selectedFilters.includes(CareActivityLogFilterType.EnrollmentEvent)) ||
        (item.data.manageMembershipRequestType === ManageMembershipRequestType.Note &&
          selectedFilters.includes(CareActivityLogFilterType.Note))
      );
    } else if (item.data.type === ActivityLogModel.CareEnrollmentPayment) {
      return selectedFilters.includes(CareActivityLogFilterType.CareEnrollmentPayment);
    } else if (item.data.type === ActivityLogModel.CareBenefitUsage) {
      return selectedFilters.includes(CareActivityLogFilterType.BenefitUsage);
    } else if (item.data.type === ActivityLogModel.CarePlanEnrollment) {
      return selectedFilters.includes(CareActivityLogFilterType.EnrollmentEvent);
    }
    return false;
  });
};

export const useCareActivityLogFilters = ({
  items,
  take,
}: CareActivityLogFiltersInput): CareActivityLogFiltersOutput => {
  const [selectedFilters, setSelectedFilters] = useState<CareActivityLogFilterType[]>([
    CareActivityLogFilterType.EnrollmentEvent,
    CareActivityLogFilterType.CareEnrollmentPayment,
    CareActivityLogFilterType.BenefitUsage,
    CareActivityLogFilterType.Note,
  ]);

  const isAllFiltersSelected = selectedFilters.length === Object.keys(CareActivityLogFilterType).length;

  const handleFilterChange = useCallback((filter: CareActivityLogFilterType) => {
    setSelectedFilters((prevFilters) => {
      if (prevFilters.includes(filter)) {
        return prevFilters.filter((f) => f !== filter);
      }
      return [...prevFilters, filter];
    });
  }, []);

  const onSelectAllFilters = useCallback(() => {
    setSelectedFilters([
      CareActivityLogFilterType.EnrollmentEvent,
      CareActivityLogFilterType.CareEnrollmentPayment,
      CareActivityLogFilterType.BenefitUsage,
      CareActivityLogFilterType.Note,
    ]);
  }, []);

  const onClearAllFilters = useCallback(() => {
    setSelectedFilters([]);
  }, []);

  const { filteredItemsTotalCount, filteredItems } = useMemo(() => {
    const _items = filterItems(items, selectedFilters);
    return { filteredItemsTotalCount: _items.length, filteredItems: _items.slice(0, take) };
  }, [items, selectedFilters, take]);

  return {
    filteredItems,
    filteredItemsTotalCount,
    selectedFilters,
    isAllFiltersSelected,
    handleFilterChange,
    onSelectAllFilters,
    onClearAllFilters,
  };
};
